import React, { useState, useEffect, useMemo } from 'react';
import { labelForStrategy } from '../utils/strategyLabels';
import './StrategySummaryTable.css';
import { API_ENDPOINTS } from '../config';
import { isMembershipAccessDenied, membershipDeniedMessage } from '../utils/userFacingStrategyErrors';
import { useDelayedTrueWhile } from '../hooks/useDelayedTrueWhile';
import { getVectorApiHeaders } from '../utils/authFetchHeaders';

const SUMMARY_LOADING_UI_DELAY_MS = 1000;

function StrategySummaryTable({
  user, contractType, contractSize, portfolioAnalyticsUrl,
  // Strategy Settings renames, keyed by DATA name (from /api/sheets). Rows are
  // fetched from /api/portfolio-analytics, which returns raw data names, so
  // the rename is applied here at render time.
  sheetLabels = {},
  internalStrategyNames = [],
  isAdminActual = false,
  adminViewAs = 'admin',
}) {
  const internalStrategySet = useMemo(
    () => new Set((internalStrategyNames || []).map((n) => String(n).trim().toLowerCase())),
    [internalStrategyNames],
  );
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const showSummaryLoadingUi = useDelayedTrueWhile(loading, SUMMARY_LOADING_UI_DELAY_MS);
  const [sortBy, setSortBy] = useState('name'); // name, netTotal, winRate, etc.
  const [sortOrder, setSortOrder] = useState('asc'); // asc or desc
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const fetchSummarySheet = async () => {
      // Wait for user to be fully loaded - check for valid email string
      if (!user || !user.email || typeof user.email !== 'string' || user.email.trim() === '') {
        // If user is explicitly null/undefined, keep loading
        // If user exists but no valid email, it might still be loading
        if (user === null || user === undefined) {
          setLoading(true);
        } else {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const analyticsUrl =
          portfolioAnalyticsUrl ||
          `${API_ENDPOINTS.analyze.replace('/api/analyze', '/api/portfolio-analytics')}?email=${encodeURIComponent(user.email.trim())}`;
        const resp = await fetch(analyticsUrl, {
          headers: getVectorApiHeaders({ isAdminActual, adminViewAs }),
        });
        const contentType = resp.headers.get('content-type') || '';
        const isJson = contentType.toLowerCase().includes('application/json');
        const data = isJson ? await resp.json() : { success: false, error: await resp.text() };
        
        if (isMembershipAccessDenied(resp, data)) {
          setSummaryData([]);
          setError(membershipDeniedMessage(data));
          setLoading(false);
          return;
        }

        if (resp.status === 401 && data.requiresOAuth) {
          // User needs to authenticate - set empty data and stop loading
          // The parent component will handle showing the auth message
          setSummaryData([]);
          setLoading(false);
          return;
        }
        
        if (!data.success || !Array.isArray(data.strategies)) {
          throw new Error(data.error || 'Failed to load strategy summary data');
        }

        if (data.strategies.length === 0) {
          setSummaryData([]);
          setLoading(false);
          return;
        }

        const toRatio = (percent) => {
          const num = Number(percent) || 0;
          return num / 100;
        };

        const parsedData = data.strategies
          .map((item) => {
            const statsByDay = item?.statsByDay || {};
            const overall = statsByDay.Overall || {};
            const netTotal = Number.isFinite(Number(overall.netPL))
              ? Number(overall.netPL)
              : (Number(overall.wins) || 0) - (Number(overall.losses) || 0);

            return {
              name: item?.strategy || '',
              displayName: item?.displayName || '',
              metrics: {
                netTotalPL: netTotal,
                avgWin: Number(overall.avgWin) || 0,
                avgLoss: Number(overall.avgLoss) || 0,
                totalWinRate: toRatio(overall.winRate),
                weekdayWinRates: {
                  Monday: toRatio(statsByDay?.Monday?.winRate),
                  Tuesday: toRatio(statsByDay?.Tuesday?.winRate),
                  Wednesday: toRatio(statsByDay?.Wednesday?.winRate),
                  Thursday: toRatio(statsByDay?.Thursday?.winRate),
                  Friday: toRatio(statsByDay?.Friday?.winRate),
                }
              }
            };
          })
          .filter((entry) => entry.name && entry.name.trim() !== '');

        setSummaryData(parsedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching summary data:', err);
        setSummaryData([]);
        setError(err.message || 'Failed to load strategy summary data');
        setLoading(false);
      }
    };

    fetchSummarySheet();
  }, [user, portfolioAnalyticsUrl, isAdminActual, adminViewAs]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc'); // Default to descending for numbers
    }
  };

  // The name a row shows: the server's display name, else a rename from /api/sheets.
  const shownName = (strategy) => strategy.displayName || sheetLabels[strategy.name]
    || labelForStrategy(strategy.name) || strategy.name;

  const sortedData = [...summaryData].sort((a, b) => {
    let aVal, bVal;

    switch (sortBy) {
      case 'name':
        return sortOrder === 'asc' 
          ? shownName(a).localeCompare(shownName(b))
          : shownName(b).localeCompare(shownName(a));
      
      case 'netTotal':
        aVal = a.metrics?.netTotalPL || 0;
        bVal = b.metrics?.netTotalPL || 0;
        break;
      
      case 'winRate':
        aVal = a.metrics?.totalWinRate || 0;
        bVal = b.metrics?.totalWinRate || 0;
        break;
      
      case 'avgWin':
        aVal = a.metrics?.avgWin || 0;
        bVal = b.metrics?.avgWin || 0;
        break;
      
      case 'avgLoss':
        aVal = a.metrics?.avgLoss || 0;
        bVal = b.metrics?.avgLoss || 0;
        break;
      
      case 'monday':
        aVal = a.metrics?.weekdayWinRates?.Monday || 0;
        bVal = b.metrics?.weekdayWinRates?.Monday || 0;
        break;
      
      case 'tuesday':
        aVal = a.metrics?.weekdayWinRates?.Tuesday || 0;
        bVal = b.metrics?.weekdayWinRates?.Tuesday || 0;
        break;
      
      case 'wednesday':
        aVal = a.metrics?.weekdayWinRates?.Wednesday || 0;
        bVal = b.metrics?.weekdayWinRates?.Wednesday || 0;
        break;
      
      case 'thursday':
        aVal = a.metrics?.weekdayWinRates?.Thursday || 0;
        bVal = b.metrics?.weekdayWinRates?.Thursday || 0;
        break;
      
      case 'friday':
        aVal = a.metrics?.weekdayWinRates?.Friday || 0;
        bVal = b.metrics?.weekdayWinRates?.Friday || 0;
        break;
      
      default:
        return 0;
    }

    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 'N/A';
    // Scale values based on contract type and size
    // Summary sheet has NQ values, so scale accordingly
    const nqMultiplier = contractType === 'MNQ' ? 0.1 : 1; // MNQ is 1/10th of NQ
    const scaledValue = value * nqMultiplier * contractSize;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(scaledValue);
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading && showSummaryLoadingUi) {
    return (
      <div className="summary-table-loading">
        Loading strategy comparison data...
      </div>
    );
  }

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="results-spreadsheet-error" role="alert">{error}</div>
    );
  }

  if (summaryData.length === 0) {
    return (
      <div className="summary-table-empty">
        No strategy data available right now. Please try again shortly.
      </div>
    );
  }

  return (
    <div className="strategy-summary-container">
      <div className="summary-table-wrapper portfolio-simulator-table-wrapper">
        <table className="portfolio-simulator-table summary-portfolio-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th onClick={() => handleSort('name')} className="sortable">
                Strategy {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('netTotal')} className="sortable">
                {isMobile ? 'P&L' : 'Net Total'} {sortBy === 'netTotal' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('avgWin')} className="sortable">
                {isMobile ? 'Avg W' : 'Avg Win'} {sortBy === 'avgWin' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('avgLoss')} className="sortable">
                {isMobile ? 'Avg L' : 'Avg Loss'} {sortBy === 'avgLoss' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('winRate')} className="sortable">
                {isMobile ? 'WR%' : 'Win Rate'} {sortBy === 'winRate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('monday')} className="sortable">
                Monday {sortBy === 'monday' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('tuesday')} className="sortable">
                Tuesday {sortBy === 'tuesday' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('wednesday')} className="sortable">
                Wednesday {sortBy === 'wednesday' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('thursday')} className="sortable">
                Thursday {sortBy === 'thursday' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('friday')} className="sortable">
                Friday {sortBy === 'friday' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((strategy, index) => {
              const metrics = strategy.metrics;
              const netTotal = metrics?.netTotalPL || 0;
              const isPositive = netTotal >= 0;
              const isInternal = internalStrategySet.has(String(strategy.name || '').trim().toLowerCase());

              return (
                <tr
                  key={index}
                  className={`${isPositive ? 'positive' : 'negative'}${isInternal ? ' is-internal' : ''}`}
                >
                  <td className="rank-cell">{index + 1}</td>
                  <td className="strategy-name-cell">
                    <span className="strategy-name-link">
                      {shownName(strategy)}
                    </span>
                    {isInternal ? (
                      <span className="summary-internal-badge" title="Internal — admins only">Internal</span>
                    ) : null}
                  </td>
                  <td className={`net-total ${isPositive ? 'positive' : 'negative'}`}>
                    {formatCurrency(netTotal)}
                  </td>
                  <td className="positive">{formatCurrency(metrics?.avgWin || 0)}</td>
                  <td className="negative">{formatCurrency(metrics?.avgLoss || 0)}</td>
                  <td className="win-rate">{formatPercent(metrics?.totalWinRate || 0)}</td>
                  <td className="day-win-rate">{formatPercent(metrics?.weekdayWinRates?.Monday || 0)}</td>
                  <td className="day-win-rate">{formatPercent(metrics?.weekdayWinRates?.Tuesday || 0)}</td>
                  <td className="day-win-rate">{formatPercent(metrics?.weekdayWinRates?.Wednesday || 0)}</td>
                  <td className="day-win-rate">{formatPercent(metrics?.weekdayWinRates?.Thursday || 0)}</td>
                  <td className="day-win-rate">{formatPercent(metrics?.weekdayWinRates?.Friday || 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="summary-table-info">
        <p>Click column headers to sort. Showing data for {summaryData.length} strategies.</p>
      </div>
    </div>
  );
}

export default StrategySummaryTable;
